import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { BASE_INDI_URL } from 'src/app/injection-tokens';
import { NewSalesorderOrderlinesComponent } from './new-salesorder-orderlines/new-salesorder-orderlines.component';
import { NewSalesorderShippingDetailsComponent } from './new-salesorder-shipping-details/new-salesorder-shipping-details.component';
import { NewSalesorderInvoiceDetailsComponent } from './new-salesorder-invoice-details/new-salesorder-invoice-details.component';
import { NewSalesorderInfoComponent } from './new-salesorder-info/new-salesorder-info.component';
import { ActivatedRoute, Router } from '@angular/router';
import { FeedbackSettings } from 'src/app/general-components/feedback/feedback-settings';
import { FeedbackType } from 'src/app/general-components/feedback/feedback-type';
import { Profile } from 'src/app/general-components/profile/profile';
import { AuthService } from 'src/app/auth/auth.service';
import { SHIPPING_METHOD_CODES } from './shipping-methods.const';
import { HttpErrorResponse } from '@angular/common/http';
import { Address, IndiCustomer } from '../indi-customer';
import { CombinedData, NewSalesorderService } from './new-salesorder.service';
import { ApiResponse } from '../api-response';
import { APIVerkooporder } from '../APIVerkooporders';
import { APIVerkoopordersToWrite } from '../APIVerkoopordersToWrite';
import { User } from '../user';
import { ManuallyCreatedRequest } from '../manually-created-request';

@Component({
  selector: 'app-new-salesorder',
  templateUrl: './new-salesorder.component.html',
  styleUrls: ['./new-salesorder.component.scss'],
})
export class NewSalesorderComponent implements OnInit, AfterViewInit {
  profile: Profile | undefined;
  APIVerkooporder: APIVerkooporder = { ticketId: 0, orderDetails: {} } as APIVerkooporder;
  indiCustomer: IndiCustomer = {} as IndiCustomer;
  users: User[] = [];
  apiResponse?: ApiResponse;
  form!: FormGroup;
  ids: number[] = [];
  ticketIds: number[] = [];
  isLoading: boolean = true;
  manuallyCreatedIsLoading: boolean = false;
  isSent: boolean = false;
  orderError?: HttpErrorResponse;
  shippingAddresses!: Address[];
  nightShippingAddress!: Address;
  defaultInvoiceAddress!: Address;
  invoiceEmailAddress: string = '';
  icmNote: string = '';

  @ViewChild(NewSalesorderOrderlinesComponent)
  orderlinesComponent?: NewSalesorderOrderlinesComponent;
  @ViewChild(NewSalesorderShippingDetailsComponent)
  shippingComponent?: NewSalesorderShippingDetailsComponent;
  @ViewChild(NewSalesorderInvoiceDetailsComponent)
  invoiceComponent?: NewSalesorderInvoiceDetailsComponent;
  @ViewChild(NewSalesorderInfoComponent)
  infoComponent?: NewSalesorderInfoComponent;

  feedbackSettings: FeedbackSettings = {
    identifier: 'generalFeedback',
    show: false,
    type: FeedbackType.error,
    message: '',
    closeButton: false,
  };

  constructor(
    @Inject(BASE_INDI_URL) readonly baseIndiUrl: string,
    private route: ActivatedRoute,
    private router: Router,
    private service: NewSalesorderService,
    private fb: FormBuilder,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.profile = this.authService.getUserInfo();
    this.ids = this.route.snapshot.queryParamMap.getAll('id')
      .map(id => Number(id))
      .filter(id => !isNaN(id) && id !== 0);
    this.form = this.createFormGroup();
  }

  ngAfterViewInit(): void {
    if (this.ids.length === 0) {
      this.updateFeedback(
        'Geen geldige id gegeven',
        FeedbackType.error,
        false,
        true
      );
      this.isLoading = false;
      this.orderError = undefined;
    } else {
      this.service.getCombinedData(this.ids).subscribe(
        (combinedData: CombinedData) => {
          if (combinedData.APIVerkooporders.verkooporder.manuallyCreated) {
            this.updateFeedback(
              '(Een deel van) deze order is al handmatig aangemaakt!',
              FeedbackType.error,
              false,
              true,
              'Terug naar het overzicht'
            );
          }
          this.ticketIds = combinedData.APIVerkooporders.ticketIds;
          this.APIVerkooporder = combinedData.APIVerkooporders.verkooporder;
          this.indiCustomer = combinedData.indiCustomer;
          this.users = combinedData.users;
          this.icmNote = combinedData.ICMCustomerNotes.notes;
          this.initializeAddresses();
          this.initializeForm();
          this.isLoading = false;
        },
        (error: any) => {
          console.error('Failed to get salesorder information', error);
          this.updateFeedback(
            'Er is iets fout gegaan bij het ophalen van de verkooporder' +
              (error.error?.exception ? ': \n' + error.error?.exception : ''),
            FeedbackType.error,
            false,
            true
          );
          this.isLoading = false;
        }
      );
    }
  }

  initializeAddresses(): void {
    this.defaultInvoiceAddress = this.indiCustomer.addresses
      .filter((address => address.type === 'atDefaultForInvoice'
        || address.type === 'atDefaultForInvoiceAndShipping'))[0];

    this.nightShippingAddress = this.indiCustomer.addresses.filter(address => address.nightShipping)[0];

    this.shippingAddresses = this.indiCustomer.addresses
      .filter((address => address.type !== 'atDefaultForInvoice'
        && address.type !== 'atForInvoice'));
  }

  createFormGroup(): FormGroup {
    return this.fb.group({
      orderlines: this.fb.array([]),
      shippingDetails: this.fb.group({}),
      invoiceDetails: this.fb.group({}),
      info: this.fb.group({}),
    });
  }

  initializeForm(): void {
    this.setOrderlines();
    this.setShippingDetails();
    this.shippingComponent?.initializeAddressData();
    this.setInvoiceDetails();
    this.setInfo();
  }

  setOrderlines(): void {
    this.form.setControl(
      'orderlines',
      this.orderlinesComponent!.createOrderlines(
        this.APIVerkooporder.orderDetails.orderlines
      )
    );
  }

  setShippingDetails(): void {
    this.form.setControl(
      'shippingDetails',
      this.shippingComponent!.createShippingDetails(
        this.APIVerkooporder.orderDetails.deliveryAddress
      )
    );
  }

  setInvoiceDetails(): void {
    this.form.setControl(
      'invoiceDetails',
      this.invoiceComponent!.createInvoiceDetails(
        this.APIVerkooporder.orderDetails.customer.invoiceEmailAddress,
        this.defaultInvoiceAddress
      )
    );
  }

  setInfo(): void {
    this.form.setControl(
      'info',
      this.infoComponent!.createInfo(this.APIVerkooporder.orderDetails.customerRef || '')
    );
  }

  get orderlines(): FormArray {
    return this.form.get('orderlines') as FormArray;
  }

  get shippingDetails(): FormGroup {
    return this.form.get('shippingDetails') as FormGroup;
  }

  get invoiceDetails(): FormGroup {
    return this.form.get('invoiceDetails') as FormGroup;
  }

  get info(): FormGroup {
    return this.form.get('info') as FormGroup;
  }

  handleInvoiceEmailChange(newEmail: string): void {
    this.invoiceEmailAddress = newEmail;
  }

  onSubmit(): void {
    const salesorders: APIVerkoopordersToWrite = this.makeAPIVerkoopordersToWrite();

    this.isLoading = true;
    this.orderError = undefined;
    this.service.postAPIVerkoopordersToWrite(salesorders).subscribe(
      (apiResponse: ApiResponse) => {
        this.apiResponse = apiResponse;
        this.isLoading = false;
      },
      (error: HttpErrorResponse) => {
        console.error("Failed to submit the salesorder to the backend", error);
        this.isLoading = false;
        this.orderError = error;
      }
    )
  }

  makeAPIVerkoopordersToWrite(): APIVerkoopordersToWrite {
    return {
      ids: this.ids,
      verkooporder: {
        orderDetails: {
          customer: {
            invoiceEmailAddress: this.invoiceComponent!.getEmailAddress(),
            userUUID: this.shippingComponent!.getUserUUID(),
          },
          customerRef: this.infoComponent!.getOrderReference(),
          shippingMethod: SHIPPING_METHOD_CODES[this.shippingComponent!.getShippingMethod()],
          fixedDeliveryDate: this.infoComponent!.getFixedDeliveryDate(),
          deliveryAddress: this.shippingComponent!.getDeliveryAddress(),
          orderlines: this.orderlinesComponent!.getOrderlines(),
        },
        reason: this.infoComponent!.getReason(),
        assessmentNotes: this.infoComponent!.getAssessmentNotes(),
        createdByHint: this.profile?.firstName + ' ' + this.profile?.lastName,
      }
    }
  }

  markVerkoopordersAsManuallyCreated(): void {
  if (confirm('Weet je zeker dat je deze verkooporder(s) wil markeren als handmatig aangemaakt?')) {
    this.manuallyCreatedIsLoading = true;
    const request: ManuallyCreatedRequest = {
      ids: this.ids,
      updatedByHint: this.profile?.firstName + ' ' + this.profile?.lastName,
    };
    this.service
      .markVerkoopordersAsManuallyCreated(request)
      .pipe(finalize(() => (this.manuallyCreatedIsLoading = false)))
      .subscribe({
        next: () => {
          this.updateFeedback(
            `Gelukt! De verkooporder${
              this.ids.length > 1 ? 's zijn' : ' is'
            } gemarkeerd als handmatig aangemaakt.`, //
            FeedbackType.succes,
            false,
            true,
            'Terug naar het overzicht'
          );
        },
        error: (err) => {
          console.error(
            'Failed to mark verkooporders as manually created',
            err
          );
          this.updateFeedback(
            'Er is iets misgegaan',
            FeedbackType.error,
            false,
            true,
            'Terug naar het overzicht'
          );
        },
      });
    }   
  }

  updateFeedback(
    message: string,
    type: FeedbackType,
    closeButton: boolean,
    show: boolean,
    buttonText?: string
  ) {
    this.feedbackSettings.message = message;
    this.feedbackSettings.type = type;
    this.feedbackSettings.closeButton = closeButton;
    this.feedbackSettings.show = show;
    this.feedbackSettings.buttonText = buttonText;
  }

  hideOrderError(): void {
    this.orderError = undefined;
  }

  toOverview(): void {
    this.router.navigate(['/verkooporders/verkooporder-overzicht']);
  }
}
